import { useEffect, useMemo, useRef, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Card,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { z } from 'zod';

import { SlideInModalDialogStyled } from '@/components';
import {
  Allergen,
  convertFormRecordToActionIngredientArray,
  IngredientWithAmount,
  languageSchema,
  onQueryError,
  onQuerySuccess,
  QuizAnswer,
  useProduct,
} from '@/core';
import {
  AllergenPicker,
  OverrideUpdate,
  RecipeOverrideUpdate,
  TableRowIngredient,
} from '@/features';
import { FormulaTable } from '@/features/product/components/variants/FormulaTable';

type DialogEditIngredientsProps = {
  answer: QuizAnswer;
  open: boolean;
  setOpen: (open: boolean) => void;
};

export type IngredientToBeModified = {
  id: number;
  amount_change: number;
};

export type DialogEditIngredientsFormData = {
  ingredientsToBeRemoved: number[];
  ingredientsToBeModified: Record<string, { amount: string }>;
  allergens: Allergen[];
};

const schema = z.object({
  ingredientsToBeRemoved: z.array(z.number()),
  ingredientsToBeModified: z.record(z.string(), z.object({ amount: z.string() })),
  allergens: z.array(z.object({ id: z.number(), name: languageSchema })),
});

export const DialogEditIngredients = ({ answer, open, setOpen }: DialogEditIngredientsProps) => {
  const [addIngredientsState, setAddIngredientsState] = useState<IngredientWithAmount[]>([]);
  const {
    queries: { useProductRecipeOverrides, useProductIngredients },
    mutations: { updateProductRecipeOverrideMutation },
  } = useProduct();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { data: ingredientsResponse } = useProductIngredients(Number(id), open);
  const { data: initialActions } = useProductRecipeOverrides(answer.id);
  const hasInitialized = useRef(false);
  const wasIngredientAdded = useMemo(() => addIngredientsState.length > 0, [addIngredientsState]);

  const methods = useForm<DialogEditIngredientsFormData>({
    defaultValues: {
      ingredientsToBeRemoved: [],
      ingredientsToBeModified: {},
      allergens: [],
    },
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    // Initialize form with initial values (actions)
    if (ingredientsResponse?.data && initialActions?.data && !hasInitialized.current) {
      methods.reset({
        ingredientsToBeRemoved: initialActions.data.ingredient_overrides
          .filter((o) => o.amount_change === null)
          .map((o) => o.ingredient.id),
        ingredientsToBeModified: ingredientsResponse.data.reduce(
          (acc, ingredient) => {
            const existing = initialActions.data.ingredient_overrides.find(
              (a) => a.ingredient.id === ingredient.ingredient.id,
            );
            acc[ingredient.ingredient.id] = {
              amount:
                existing?.amount_change === null
                  ? '0'
                  : String(Number(ingredient.amount) + Number(existing?.amount_change ?? 0)),
            };
            return acc;
          },
          {} as Record<string, { amount: string }>,
        ),
        allergens: initialActions.data.allergens,
      });

      setAddIngredientsState(
        initialActions.data.ingredient_overrides
          .filter((o) => {
            const productIngredientIds = ingredientsResponse.data.map((ir) => ir.ingredient.id);
            return o.amount_change !== null && !productIngredientIds.includes(o.ingredient.id);
          })
          .map((o) => ({
            ingredient: {
              ...o.ingredient,
              price: '0',
              description: '',
              is_active: true,
            },
            amount: o.amount_change ?? 0,
          })),
      );
      hasInitialized.current = true;
    }
  }, [ingredientsResponse?.data, initialActions?.data, methods.reset, methods]);

  const { getValues } = methods;

  const onSubmit = async (data: DialogEditIngredientsFormData) => {
    const modifiedIngredientsArray: OverrideUpdate[] = convertFormRecordToActionIngredientArray(
      data.ingredientsToBeModified,
      ingredientsResponse?.data ?? [],
    );

    const ingredientsToBeRemovedApiForm = data.ingredientsToBeRemoved.map((ingredientId) => ({
      ingredient_id: ingredientId,
      amount_change: null,
    }));

    const modifiedData: RecipeOverrideUpdate = {
      ingredient_overrides: modifiedIngredientsArray.concat(ingredientsToBeRemovedApiForm),
      allergen_ids: data.allergens.map((a) => a.id),
    };

    addIngredientsState.forEach((ingredient) => {
      if (ingredient.amount === null) return;
      if (!Number(ingredient.amount)) return;
      modifiedData.ingredient_overrides.push({
        amount_change: Number(ingredient.amount),
        ingredient_id: ingredient.ingredient.id,
      });
    });

    const updateResponse = await updateProductRecipeOverrideMutation.mutateAsync({
      answerId: answer.id,
      data: modifiedData,
    });
    if (updateResponse.status === 200 && updateResponse.data) {
      onQuerySuccess({
        queryKeysToInvalidate: [['productRecipeOverrides', answer.id]],
        successMessage: t('VARIANT.RECIPE_OVERRIDE_UPDATE_SUCCESS'),
        queryClient,
        callback: () => {
          setOpen(false);
        },
      });
    } else {
      onQueryError({ errorMessage: t('VARIANT.RECIPE_OVERRIDE_UPDATE_FAILURE') });
    }
  };

  return (
    <FormProvider {...methods}>
      <SlideInModalDialogStyled
        open={open}
        setOpen={setOpen}
        title={t('VARIANT.RECIPE_OVERRIDES_TITLE')}
        onActionButtonClick={() => onSubmit(getValues())}
        actionButtonLabel={t('GLOBAL.SAVE')}
      >
        <Stack direction="column" gap={3}>
          <Stack direction="column" gap={1}>
            <Typography variant="body1" color="text.secondary">
              {t('VARIANT.RECIPE_OVERRIDE_MODIFY')}
            </Typography>
            <Card>
              {ingredientsResponse?.data && ingredientsResponse.data.length > 0 ? (
                <>
                  <Typography variant="body2" m={'12px 0 12px 110px'} fontWeight={500}>
                    {t('INGREDIENT.INGREDIENTS')}
                  </Typography>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: '1%' }}></TableCell>
                        <TableCell>{t('HEADERS.NAME')}</TableCell>
                        <TableCell sx={{ width: '17%' }}></TableCell>
                        <TableCell sx={{ width: '20%' }}>
                          {t('VARIANT.WEIGHT_AND_COUNT_COLUMN')}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody sx={{ '.MuiTableCell-root': { py: 1 } }}>
                      {ingredientsResponse?.data.map((ingredientWithAmount) => (
                        <TableRowIngredient
                          ingredientWithAmount={ingredientWithAmount}
                          key={ingredientWithAmount.ingredient.id}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </>
              ) : (
                <Typography variant="body2" my={2} fontWeight={500}>
                  {t('PRODUCT.NO_INGREDIENTS')}
                </Typography>
              )}
            </Card>
          </Stack>
          <Stack direction="column" gap={1}>
            <Typography variant="body1" color="text.secondary">
              {t('VARIANT.RECIPE_OVERRIDE_ADD')}
            </Typography>
            <Card>
              {wasIngredientAdded && (
                <Typography variant="body2" m={'12px 0 12px 110px'} fontWeight={500}>
                  {t('INGREDIENT.INGREDIENTS')}
                </Typography>
              )}
              <FormulaTable
                ingredientsWithAmount={addIngredientsState}
                setIngredientsWithAmount={setAddIngredientsState}
              />
            </Card>
          </Stack>
          <AllergenPicker
            label={t('VARIANT.SELECT_ALLERGENS_FOR_VARIANT')}
            sx={{ maxWidth: 'undefined' }}
          />
        </Stack>
      </SlideInModalDialogStyled>
    </FormProvider>
  );
};
